import { useCallback, useEffect, useState } from 'react';
import { Outlet, useLocation, useOutletContext, useParams, useSearchParams } from 'react-router-dom';
import { api, ApiError } from '../api';
import { useToast } from '../toast';
import { PageHeader, SectionHeader } from '../components/PageHeader';
import type { Channel } from '../channels';
import { ORG_SECTIONS, orgPath, sectionFor, useNav, workspacePath } from '../nav';
import { useSession } from '../session';

export type Kind = 'shared' | 'personal';

export interface Identity {
  id: string;
  username: string;
  kind: Kind;
  userEmail: string | null;
  verified: boolean;
  lastMintOkAt: string | null;
  lastMintError: string | null;
  disabledAt: string | null;
  channels: Channel[];
  licenseKey: string | null;
  licenseName: string | null;
}

export interface Grant {
  id: string;
  identityId: string;
  username: string;
  userId: string;
  userEmail: string;
  userName: string;
  endsAt: string | null;
  channels: Channel[];
  grantedByEmail: string;
  createdAt: string;
}

export interface OrgDetail {
  org: {
    id: string;
    label: string;
    environment: string;
    status: string;
    statusDetail: string | null;
    myDomainUrl: string;
    sfOrgId: string;
    disabledAt: string | null;
    channels: Channel[];
    handoverTarget: string | null;
  };
  tenantChannels: Channel[];
  role: 'owner' | 'admin' | 'member';
  setupReady: boolean;
  identities: Identity[];
  grants: Grant[];
}

export interface Member {
  userId: string;
  email: string;
  displayName: string;
  status: string;
}

export interface OrgContextValue {
  tenantId: string;
  orgId: string;
  detail: OrgDetail;
  canManage: boolean;
  orgDisabled: boolean;
  members: Member[];
  busy: boolean;
  act: (fn: () => Promise<unknown>, done?: string) => Promise<void>;
  reconnect: () => void;
  setError: (message: string | null) => void;
}

export function useOrg(): OrgContextValue {
  return useOutletContext<OrgContextValue>();
}

/**
 * One connected Salesforce org.
 *
 * Everything about an org used to live on this page at once - what it may be used for, who Kruvi Access may act
 * as, who may use those identities, the handover instruction and the disable button - so an admin granting
 * somebody access scrolled past four unrelated decisions to reach the one they came for. Each of those is
 * now its own page in the side menu, under the org's name, and the shell keeps only what stays true
 * whichever of them you are on.
 */
export function Org() {
  const { tenantId = '', orgId = '' } = useParams();
  const { pathname } = useLocation();
  const { me } = useSession();
  const { orgs: menuOrgs, refreshOrgs } = useNav();
  const [params, setParams] = useSearchParams();
  const [detail, setDetail] = useState<OrgDetail | null>(null);
  const [members, setMembers] = useState<Member[]>([]);
  const [error, setError] = useState<string | null>(null);
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const d = await api<OrgDetail>(`/api/tenants/${tenantId}/orgs/${orgId}`);
      setDetail(d);
      if (d.role !== 'member') {
        const m = await api<{ members: Member[] }>(`/api/tenants/${tenantId}/members`);
        setMembers(m.members.filter((x) => x.status === 'active'));
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load this org');
    }
  }, [tenantId, orgId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (params.get('connected') === '1') {
      toast.ok('Connected. Now pick the users Kruvi Access may act as, then grant access.');
      params.delete('connected');
      setParams(params, { replace: true });
    }
  }, [params, setParams, toast]);

  /**
   * Every action on this org and its pages. The result goes to a toast rather than into the page: these
   * pages are long, the buttons are partway down them, and a notice rendered at the top is a notice you
   * have to go and look for.
   */
  const act = useCallback(
    async (fn: () => Promise<unknown>, done?: string) => {
      setBusy(true);
      setError(null);
      try {
        await fn();
        if (done) toast.ok(done);
        await load();
        // A rename, a disable or a removal changes what the menu shows, not just this page.
        void refreshOrgs();
      } catch (err) {
        toast.error(err instanceof ApiError ? err.message : 'That did not work');
      } finally {
        setBusy(false);
      }
    },
    [load, refreshOrgs, toast],
  );

  // The menu is the way to this org's other pages, so an org it does not list yet - connected a moment ago,
  // or granted since the menu loaded - is fetched into it once this page has proved the org is there.
  const listed = menuOrgs?.some((o) => o.id === orgId) ?? true;
  useEffect(() => {
    if (detail && !listed) void refreshOrgs();
  }, [detail, listed, refreshOrgs]);

  const reconnect = useCallback(() => {
    void act(async () => {
      // The label goes back unchanged: a reconnect is not a rename.
      const r = await api<{ authorizeUrl: string }>(`/api/tenants/${tenantId}/orgs/connect`, {
        method: 'POST',
        body: { environment: detail?.org.environment ?? 'production', label: detail?.org.label },
      });
      window.location.href = r.authorizeUrl;
    });
  }, [act, tenantId, detail?.org.environment, detail?.org.label]);

  if (!detail) return error ? <div className="alert">{error}</div> : <p className="muted">Loading…</p>;

  const { org } = detail;
  const canManage = detail.role === 'owner' || detail.role === 'admin';
  const orgDisabled = org.disabledAt !== null;
  const base = `/workspaces/${tenantId}/orgs/${orgId}`;

  const context: OrgContextValue = {
    tenantId,
    orgId,
    detail,
    canManage,
    orgDisabled,
    members,
    busy,
    act,
    reconnect,
    setError,
  };

  const section = sectionFor(ORG_SECTIONS, base, pathname);
  const workspaceName = me?.memberships.find((m) => m.tenantId === tenantId)?.name ?? 'Workspace';

  return (
    <>
      <PageHeader
        crumbs={[
          { label: workspaceName, to: workspacePath(tenantId) },
          { label: 'Salesforce orgs', to: workspacePath(tenantId) },
          { label: org.label, to: orgPath(tenantId, orgId) },
        ]}
        title={org.label}
        meta={
          <>
            <span className={`pill ${org.status === 'active' ? 'on' : org.status === 'pending' ? '' : 'off'}`}>
              {org.status}
            </span>
            <span className={`env-tag ${org.environment}`}>{org.environment === 'sandbox' ? 'Sandbox' : 'Production'}</span>
            <span className="mono small">{org.sfOrgId}</span>
          </>
        }
      />

      {orgDisabled && (
        <div className="notice">
          This org was taken out of service on {new Date(org.disabledAt!).toLocaleString()}. Every grant on it
          was revoked. Its history is kept; re-enabling does not bring the grants back.
        </div>
      )}
      {error && <div className="alert">{error}</div>}
      {org.statusDetail && org.status === 'error' && (
        <div className="alert">Last health check: {org.statusDetail}</div>
      )}

      {section && <SectionHeader title={section.label} blurb={section.blurb} />}

      <Outlet context={context} />
    </>
  );
}

/**
 * Anything but the browser channel calls Salesforce directly, because Kruvi Access deliberately stays out of the
 * data path. That is a decision with a setup cost: the client doing the calling has to be able to reach the
 * org. AI clients in particular tend to allowlist outbound hosts, and an admin who has not been told this
 * first meets it as a token that mints cleanly and then cannot be used.
 */
export function DirectAccessNote({ myDomainUrl }: { myDomainUrl: string }) {
  let host = myDomainUrl;
  try {
    host = new URL(myDomainUrl).host;
  } catch {
    /* shown verbatim if it will not parse */
  }
  return (
    <div className="notice">
      <p style={{ margin: '0 0 8px' }}>
        CLI and AI agent sessions talk to Salesforce directly. Your data never passes through Kruvi Access, so the
        client has to be able to reach this org itself:
      </p>
      <p className="mono" style={{ margin: '0 0 8px' }}>{host}</p>
      <p className="small muted" style={{ margin: 0 }}>
        If your AI client restricts which domains it may call, add this host there. Clients that accept
        wildcards can take <span className="mono">*.my.salesforce.com</span> once instead, which covers every
        production org you connect; sandboxes sit under{' '}
        <span className="mono">*.sandbox.my.salesforce.com</span>.
      </p>
    </div>
  );
}
